import React from 'react'
import { Plus } from 'lucide-react'
import { FlowStepConfig } from '../../types/flowConfig.types'
import {
  ParsedSimpleCondition,
  VisualCondOp,
  buildSimpleCondition,
  getOperatorLabels,
  parseSimpleCondition
} from './conditionalExpression'
import { StepOption, StepTargetSelect } from './StepTargetSelect'

export interface ConditionalConditionsEditorProps {
  config: FlowStepConfig
  onConfigUpdate: (updates: Partial<FlowStepConfig>) => void
  availableSteps: StepOption[]
}

type CondRow = {
  condition: string
  template: string
  next_step: string
}

function defaultParts(): ParsedSimpleCondition {
  return { left: '', op: '==', right: '', numeric: false }
}

function ConditionRuleCard({
  row,
  index,
  onPatch,
  onRemove,
  availableSteps
}: {
  row: CondRow
  index: number
  onPatch: (patch: Partial<CondRow>) => void
  onRemove: () => void
  availableSteps: StepOption[]
}) {
  const raw = row.condition || ''
  const parsed = parseSimpleCondition(raw)
  const mustUseAdvanced = parsed === null && raw.trim().length > 0

  const [advanced, setAdvanced] = React.useState(mustUseAdvanced)
  React.useEffect(() => {
    setAdvanced(mustUseAdvanced)
  }, [mustUseAdvanced, index])

  const parts: ParsedSimpleCondition = parsed ?? defaultParts()

  const applyParts = (next: ParsedSimpleCondition) => {
    onPatch({ condition: buildSimpleCondition(next) })
  }

  const opOptions = getOperatorLabels()

  return (
    <div className="p-3 border border-gray-200 rounded-md space-y-3">
      <div className="flex justify-between items-center">
        <span className="text-sm font-medium text-gray-700">Condição {index + 1}</span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setAdvanced((a) => !a)}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            {advanced ? 'Usar formulário simples' : 'Expressão avançada'}
          </button>
          <button type="button" onClick={onRemove} className="text-red-600 hover:text-red-800 text-sm">
            Remover
          </button>
        </div>
      </div>

      {advanced ? (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Expressão (JavaScript / motor do fluxo)
          </label>
          <input
            type="text"
            value={raw}
            onChange={(e) => onPatch({ condition: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder='ex.: plano == "4" ou contexto.x > 1'
          />
          <p className="text-xs text-amber-700 mt-1">
            Use quando a condição for mais complexa do que um único campo e valor.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-gray-500">
            Se o valor da variável à esquerda satisfizer a comparação, segue para a etapa escolhida.
          </p>
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[120px] flex-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">Campo / variável</label>
              <input
                type="text"
                value={parts.left}
                onChange={(e) =>
                  applyParts({
                    ...parts,
                    left: e.target.value
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="ex.: plano, user_input"
                list={`cond-field-suggestions-${index}`}
              />
              <datalist id={`cond-field-suggestions-${index}`}>
                <option value="user_input" />
                <option value="plano" />
                <option value="menu_choice" />
              </datalist>
            </div>
            <div className="min-w-[160px]">
              <label className="block text-xs font-medium text-gray-600 mb-1">Comparação</label>
              <select
                value={parts.op}
                onChange={(e) =>
                  applyParts({
                    ...parts,
                    op: e.target.value as VisualCondOp
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {opOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-[100px] flex-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">Valor esperado</label>
              <input
                type="text"
                value={parts.right}
                onChange={(e) =>
                  applyParts({
                    ...parts,
                    right: e.target.value,
                    numeric: parts.numeric
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder='ex.: 4 ou sim'
              />
            </div>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={parts.numeric}
              onChange={(e) =>
                applyParts({
                  ...parts,
                  numeric: e.target.checked
                })
              }
              className="h-4 w-4 text-blue-600 rounded border-gray-300"
            />
            Comparar como número (sem aspas na expressão)
          </label>
          <p className="text-[11px] text-gray-400 font-mono break-all">
            Gerado: {buildSimpleCondition(parts) || '(vazio)'}
          </p>
        </div>
      )}

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Próxima etapa</label>
        <StepTargetSelect
          value={row.next_step || ''}
          onChange={(id) => onPatch({ next_step: id })}
          steps={availableSteps}
          emptyLabel="— Selecionar etapa —"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Mensagem (template)</label>
        <textarea
          value={row.template || ''}
          onChange={(e) => onPatch({ template: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          rows={2}
          placeholder="Texto enviado ao usuário quando esta condição for a escolhida (opcional em alguns fluxos)"
        />
      </div>
    </div>
  )
}

/**
 * Editor visual para etapas do tipo `conditional`: campo + operador + valor, e seletores de etapa.
 */
export function ConditionalConditionsEditor({
  config,
  onConfigUpdate,
  availableSteps
}: ConditionalConditionsEditorProps) {
  const conditions = (config.conditions || []) as CondRow[]

  const patchCondition = (index: number, patch: Partial<CondRow>) => {
    const next = [...conditions]
    next[index] = { ...next[index], ...patch }
    onConfigUpdate({ conditions: next })
  }

  const removeCondition = (index: number) => {
    const next = [...conditions]
    next.splice(index, 1)
    onConfigUpdate({ conditions: next })
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Condições</label>
        <div className="space-y-2">
          {conditions.map((row, index) => (
            <ConditionRuleCard
              key={index}
              row={row}
              index={index}
              onPatch={(patch) => patchCondition(index, patch)}
              onRemove={() => removeCondition(index)}
              availableSteps={availableSteps}
            />
          ))}
          <button
            type="button"
            onClick={() => {
              onConfigUpdate({
                conditions: [
                  ...conditions,
                  {
                    condition: '',
                    template: '',
                    next_step: ''
                  }
                ]
              })
            }}
            className="w-full px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <Plus className="w-4 h-4 mr-2 inline" />
            Adicionar condição
          </button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Template padrão</label>
        <textarea
          value={config.default_template || ''}
          onChange={(e) => onConfigUpdate({ default_template: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          rows={2}
          placeholder="Mensagem quando nenhuma condição acima for satisfeita"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Próxima etapa padrão</label>
        <p className="text-xs text-gray-500 mb-1">Quando nenhuma regra corresponder, o fluxo segue para:</p>
        <StepTargetSelect
          value={config.default_step || ''}
          onChange={(id) => onConfigUpdate({ default_step: id })}
          steps={availableSteps}
          emptyLabel="— Selecionar etapa —"
        />
      </div>
    </div>
  )
}
